import React, { ChangeEvent } from 'react';
import { ItemEntity } from '@entities/Item/Types';
import { ItemTypeEntity } from '@entities/ItemType/Types';
import { notifyErrorCustom } from '@shared/Widgets/ToastNotifications/ToastNotifications';
import css from './SelectionItem.module.css';
import { GetImageFromGalleries } from '@modules/Steps/Shared/Helpers/GetImageFromGalleries';
import { DEFAULT_ITEM_IMAGE } from '@shared/Constants/Images/DefaultItemImage';
import { TruncateText } from '@shared/Helpers/FormatValue/TruncateText/TruncateText';
import { FormatNumber } from '@shared/Helpers/FormatValue/FormatNumber/FormatNumber';
import { ItemAvailability } from '@modules/Steps/Shared/Widgets/Modals/ItemsSelectionModal/ItemForSelect/ItemAvailability/ItemAvailability';
import BasicButton from '@shared/Ui/Buttons/BasicButton/BasicButton';
import NumberInput from '@shared/Ui/CustomInputs/NumberInput/NumberInput';
import { GetPackagingText } from '@viewModels/ItemTypeViewModel/Services/GetPackagingText';
import { ItemTypeViewModel } from '@viewModels/ItemTypeViewModel/ItemTypeViewModel';

interface Props {
    item: ItemEntity;
    isFillDisabled: boolean;
    itemType: ItemTypeEntity;
}

export const SelectionItem: React.FC<Props> = ({
    item,
    isFillDisabled,
    itemType,
}) => {
    const { qty, qtyFromStore, id, packaging, title, price } = item;

    const itemTypeViewModel = new ItemTypeViewModel();
    const quantity = qty ?? 0;

    const isDisabled = typeof qtyFromStore === 'number' && qtyFromStore <= 0;
    const isDecreaseDisabled = isDisabled || quantity < 1;

    const handleIncreaseQuantity = () => {
        if (quantity <= qtyFromStore) {
            itemTypeViewModel.updateItemQuantity(id, quantity + 1);
        }
    };

    const handleDecreaseQuantity = () => {
        if (quantity >= 1) {
            itemTypeViewModel.updateItemQuantity(id, quantity - 1);
        }
    };

    const handleChangeQuantity = () => (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;

        if (isNaN(Number(value))) {
            return;
        }

        if (Number(value) > qtyFromStore && typeof qtyFromStore === 'number') {
            notifyErrorCustom(`Maximum allowed quantity ${qtyFromStore}`);
            return;
        }

        itemTypeViewModel.updateItemQuantity(id, Number(e.target.value));
    };

    const handleFill = () => {
        itemTypeViewModel.fillItemType(id, itemType);
    };

    const handleClear = () => {
        itemTypeViewModel.updateItemQuantity(id, 0);
    };

    return (
        <li className={css.item}>
            <div className={css.contentWrapper}>
                <div className={css.itemTitleWrapper}>
                    <img
                        className={css.image}
                        alt="item-image"
                        src={GetImageFromGalleries(
                            DEFAULT_ITEM_IMAGE,
                            item.galleries
                        )}
                    />
                    <div className={css.textWrapper}>
                        <p className={css.title}>{TruncateText(title, 20)}</p>
                        <p className={css.text}>
                            R {FormatNumber(price ?? '0')}
                        </p>
                    </div>
                </div>

                <p className={css.text}>{GetPackagingText(packaging)}</p>
            </div>
            <div className={css.buttonsWrapper}>
                <NumberInput
                    value={qty}
                    onChange={handleChangeQuantity}
                    disabled={isDisabled}
                    increase={handleIncreaseQuantity}
                    decrease={handleDecreaseQuantity}
                    decrementDisabled={isDecreaseDisabled}
                />
                {quantity > 0 ? (
                    <BasicButton
                        onClick={handleClear}
                        extraClassName={css.fillBtn}
                    >
                        Clear
                    </BasicButton>
                ) : (
                    <BasicButton
                        onClick={handleFill}
                        extraClassName={css.fillBtn}
                        disabled={isFillDisabled || isDisabled}
                    >
                        Fill
                    </BasicButton>
                )}
                <ItemAvailability
                    className={css.itemAvailability}
                    qtyFromStore={qtyFromStore}
                />
            </div>
        </li>
    );
};
